import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { AlertCircle, Copy, Edit, FileText, Loader, MapPin, Plus, Search, Users, X } from "lucide-react";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../../../../../api";
import { getDirectReportsRequest } from "../../../../redux/actions/userActions";
import {
    createMissionRequestForCollabRequest,
    duplicateMissionRequestRequest,
    getMyCreatedMissionRequestsRequest,
} from "../../../../redux/actions/missionRequestActions";
import MissionInlineForm from "./MissionInlineForm";
import "../../parametres/parametresStyles/ParamConges.css";
import "../MesrequetesStyles/DemandeConges.css";

const CREATED_AS_MANAGER = "MANAGER";

const DemandeMissionManager = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const { directReports, directReportsLoading } = useSelector((state) => state.user || {
        directReports: [],
        directReportsLoading: false,
    });
    const { loading, myCreatedRequests } = useSelector((state) => state.missionRequests || {
        loading: false,
        myCreatedRequests: [],
    });

    const [activeTab, setActiveTab] = useState("create");
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [searchCollaborateur, setSearchCollaborateur] = useState("");
    const [selectedCollaborateurs, setSelectedCollaborateurs] = useState([]);

    const headers = useMemo(() => ({
        uid: sessionStorage.getItem("uid"),
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        "X-Tenant-Id": sessionStorage.getItem("selectedCompanyId"),
        "Content-Type": "application/json",
        Accept: "application/json",
    }), []);

    useEffect(() => {
        if (user?.id) dispatch(getDirectReportsRequest(user.id));
        dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER));
    }, [dispatch, user?.id]);

    const collaborateursFiltres = useMemo(() => {
        if (!searchCollaborateur.trim()) return directReports || [];
        const term = searchCollaborateur.toLowerCase();
        return (directReports || []).filter((c) =>
            c.firstName?.toLowerCase().includes(term) ||
            c.lastName?.toLowerCase().includes(term) ||
            c.employeeCode?.toLowerCase().includes(term) ||
            c.email?.toLowerCase().includes(term)
        );
    }, [directReports, searchCollaborateur]);

    const toggleCollaborateur = (collabId) => {
        setSelectedCollaborateurs((prev) =>
            prev.includes(collabId) ? prev.filter((id) => id !== collabId) : [...prev, collabId]
        );
    };

    const handleSubmit = (payload) => {
        if (selectedCollaborateurs.length === 0) {
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: "Veuillez selectionner au moins un collaborateur",
                confirmButtonColor: "#204385",
            });
            return;
        }

        dispatch(createMissionRequestForCollabRequest({
            ...payload,
            collaborateurIds: selectedCollaborateurs,
            createdAsRole: CREATED_AS_MANAGER,
            onSuccess: () => {
                setSelectedCollaborateurs([]);
                setActiveTab("created");
                dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER));
            },
        }));
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const renderLocation = (demande) => {
        const parts = [demande.ordreMissionPays, demande.ordreMissionVille, demande.ordreMissionAdresse].filter(Boolean);
        return parts.length > 0 ? parts.join(", ") : demande.ordreMissionLieu;
    };

    const handleDuplicate = (demande) => {
        setSelectedDemande(demande);
        setShowDuplicateModal(true);
    };

    const handleDuplicateSubmit = (payload) => {
        if (!selectedDemande) return;
        dispatch(createMissionRequestForCollabRequest({
            ...payload,
            collaborateurIds: [selectedDemande.employeeId],
        }));
        setShowDuplicateModal(false);
        setSelectedDemande(null);
        dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER));
        Swal.fire({
            icon: "success",
            title: "Succès",
            text: "Ordre de mission dupliqué",
            timer: 1600,
            showConfirmButton: false,
        });
    };

    const canEditDemande = (demande) =>
        demande && !["APPROVED", "REJECTED", "CANCELLED"].includes(demande.status)
        && (demande.status === "PENDING_ADMIN" || demande.status === "PENDING_MANAGER");

    const canCancelDemande = canEditDemande;

    const handleCancel = (demande) => {
        if (!demande) return;
        Swal.fire({
            title: "Confirmation",
            text: "Voulez-vous vraiment annuler cette demande ?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, annuler",
            cancelButtonText: "Non",
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                await axios.post(`${API_BASE_URL}/api/mission-requests/requests/${demande.id}/cancel`, null, { headers });
                await Swal.fire({
                    icon: "success",
                    title: "Succès",
                    text: "Demande annulée avec succès",
                    timer: 1600,
                    showConfirmButton: false,
                });
                dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER));
            } catch (error) {
                const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Erreur annulation demande";
                Swal.fire({ icon: "error", title: "Erreur", text: message, confirmButtonColor: "#d33" });
            }
        });
    };

    const buildInitialMission = (demande) => ({
        natureMissionId: demande.natureMissionId || "",
        code: demande.ordreMissionCode || "",
        libelle: demande.ordreMissionLibelle || "",
        description: demande.ordreMissionDescription || "",
        objetMission: demande.ordreMissionObjet || "",
        dateDebut: demande.ordreMissionDateDebut || "",
        dateFin: demande.ordreMissionDateFin || "",
        zoneMission: demande.ordreMissionZoneMission || "EN_VILLE",
        pays: demande.ordreMissionPays || "",
        ville: demande.ordreMissionVille || "",
        adresse: demande.ordreMissionAdresse || "",
        engagementsCollaborateur: demande.ordreMissionEngagementsCollaborateur || "",
        workflow: demande.workflowLevel || "both",
    });

    const openEdit = (demande) => {
        setSelectedDemande(demande);
        setShowEditModal(true);
    };

    const handleEditSubmit = async (payload) => {
        if (!selectedDemande) return;
        try {
            await axios.put(
                `${API_BASE_URL}/api/mission-requests/requests/${selectedDemande.id}/ordre-mission`,
                payload.ordreMission,
                { headers }
            );
            setShowEditModal(false);
            setSelectedDemande(null);
            dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER));
            Swal.fire({
                icon: "success",
                title: "Succès",
                text: "Ordre de mission modifié",
                timer: 1600,
                showConfirmButton: false,
            });
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Erreur modification ordre de mission";
            Swal.fire({ icon: "error", title: "Erreur", text: message, confirmButtonColor: "#d33" });
        }
    };

    const renderCreatedRequests = () => (
        <div className="pcCard">
            <div className="pcBody">
                <div className="pcTopRow">
                    <div>
                        <h2 className="pcTitle">Ordres de mission créés</h2>
                        <div className="pcMuted" style={{ fontSize: 12 }}>
                            Demandes créées par vous pour vos collaborateurs.
                        </div>
                    </div>
                    <button className="pcBtn pcBtn--ghost" type="button" onClick={() => dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER))} disabled={loading}>
                        Actualiser
                    </button>
                </div>

                <div className="pcTableWrap">
                    <table className="pcTable">
                        <thead>
                        <tr>
                            <th>Code</th>
                            <th>Collaborateur</th>
                            <th>Mission</th>
                            <th>Période</th>
                            <th>Lieu</th>
                            <th>Statut</th>
                            <th className="is-right">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading && (myCreatedRequests || []).length === 0 ? (
                            <tr><td colSpan={7} className="pcEmptyCell">Chargement...</td></tr>
                        ) : (myCreatedRequests || []).length === 0 ? (
                            <tr><td colSpan={7} className="pcEmptyCell">Aucune demande créée</td></tr>
                        ) : (myCreatedRequests || []).map((demande) => (
                            <tr key={demande.id}>
                                <td><span className="pcCode" style={{ background: "#eef2ff", border: "1px solid #c7d2fe", color: "#204385" }}>{demande.ordreMissionCode}</span></td>
                                <td>
                                    <div className="pcStrong">{demande.employeeName || "-"}</div>
                                    <div className="pcMuted" style={{ fontSize: 12 }}>{demande.employeeMatricule || ""}</div>
                                </td>
                                <td>
                                    <div className="pcStrong">{demande.ordreMissionLibelle}</div>
                                    <div className="pcMuted" style={{ fontSize: 12 }}>{demande.natureMissionLibelle || ""}</div>
                                </td>
                                <td>{formatDate(demande.ordreMissionDateDebut)} - {formatDate(demande.ordreMissionDateFin)}</td>
                                <td>
                                    {renderLocation(demande) ? (
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                            <MapPin size={13} /> {renderLocation(demande)}
                                        </span>
                                    ) : "-"}
                                </td>
                                <td><span className="pcPill is-on">{demande.status}</span></td>
                                <td className="is-right">
                                    {canEditDemande(demande) && (
                                        <button className="pcIconBtn" type="button" title="Modifier" onClick={() => openEdit(demande)} disabled={loading}>
                                            <Edit size={16} />
                                        </button>
                                    )}
                                    {canCancelDemande(demande) && (
                                        <button className="pcIconBtn" type="button" title="Annuler" onClick={() => handleCancel(demande)} disabled={loading}>
                                            <AlertCircle size={16} />
                                        </button>
                                    )}
                                    <button className="pcIconBtn" type="button" title="Dupliquer" onClick={() => handleDuplicate(demande)} disabled={loading}>
                                        <Copy size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    return (
        <div className="pcPage">
            <div className="pcHeader">
                <div>
                    <h1>Demandes de mission pour mes collaborateurs</h1>
                    <p>Saisissez un ordre de mission et affectez-le a votre equipe.</p>
                </div>
            </div>

            <div className="pcWrap">
                <div className="dcTabs" style={{ marginLeft: '30px', marginBottom: 14, border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                    <button className={`dcTab ${activeTab === "create" ? "is-active" : ""}`} type="button" onClick={() => setActiveTab("create")}>
                        <Plus size={18} />
                        Créer une demande
                    </button>
                    <button className={`dcTab ${activeTab === "created" ? "is-active" : ""}`} type="button" onClick={() => { setActiveTab("created"); dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER)); }}>
                        <FileText size={18} />
                        Ordres de mission créés
                    </button>
                </div>

                {activeTab === "created" ? renderCreatedRequests() : (
                <div className="pcCard">
                    <div className="pcBody">
                        <MissionInlineForm
                            formId="managerMissionForm"
                            onSubmit={handleSubmit}
                            loading={loading || directReportsLoading}
                            submitLabel={`Envoyer ${selectedCollaborateurs.length || ""} ordre${selectedCollaborateurs.length > 1 ? "s" : ""}`}
                        >
                            <div className="pcWizStep">
                                <div className="pcWizStep__intro">
                                    <div className="pcWizStep__introIcon"><Users size={20} /></div>
                                    <div>
                                        <div className="pcWizStep__introTitle">Collaborateurs concernés</div>
                                        <div className="pcWizStep__introSub">{selectedCollaborateurs.length} collaborateur(s) sélectionné(s)</div>
                                    </div>
                                </div>

                                <div className="pcWizField is-full">
                                    <label>Rechercher un collaborateur</label>
                                    <div style={{ position: "relative" }}>
                                        <Search size={18} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
                                        <input
                                            className="pcInput"
                                            type="text"
                                            placeholder="Nom, matricule, email..."
                                            value={searchCollaborateur}
                                            onChange={(e) => setSearchCollaborateur(e.target.value)}
                                            style={{ paddingLeft: 40 }}
                                        />
                                    </div>
                                </div>

                                <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8, padding: 8 }}>
                                    {directReportsLoading ? (
                                        <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
                                            <Loader size={24} className="spin" />
                                        </div>
                                    ) : collaborateursFiltres.length === 0 ? (
                                        <div className="pcEmptyCell">Aucun collaborateur trouvé</div>
                                    ) : collaborateursFiltres.map((collab) => (
                                        <label key={collab.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 10, borderRadius: 6, cursor: "pointer", border: "1px solid #e5e7eb", marginBottom: 6 }}>
                                            <input type="checkbox" checked={selectedCollaborateurs.includes(collab.id)} onChange={() => toggleCollaborateur(collab.id)} />
                                            <div>
                                                <div className="pcStrong">{collab.firstName} {collab.lastName}</div>
                                                <div className="pcMuted" style={{ fontSize: 12 }}>{collab.employeeCode} - {collab.email}</div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </MissionInlineForm>
                    </div>
                </div>
                )}
            </div>

            {showEditModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Modifier l'ordre de mission</div>
                                <div className="pcModal__subtitle">Modification autorisée tant que la demande n'est pas approuvée ou traitée définitivement.</div>
                            </div>
                            <button
                                className="pcIconBtn pcIconBtn--close"
                                onClick={() => { setShowEditModal(false); setSelectedDemande(null); }}
                                aria-label="Fermer"
                            >
                                <X size={15} />
                            </button>
                        </div>
                        <div className="pcModal__body">
                            <MissionInlineForm
                                formId="managerEditMissionForm"
                                onSubmit={handleEditSubmit}
                                loading={loading}
                                submitLabel="Enregistrer les modifications"
                                initialMissionData={buildInitialMission(selectedDemande)}
                                initialUrgent={selectedDemande.urgent}
                                initialMotif={selectedDemande.motif}
                                showNotes={false}
                                showJustificatifs={false}
                                showComment={false}
                            />
                        </div>
                    </div>
                </div>
            )}

            {showDuplicateModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Dupliquer l'ordre de mission</div>
                                <div className="pcModal__subtitle">Duplication de l'ordre de mission pour {selectedDemande.employeeName}. Modifiez les informations avant d'envoyer la demande.</div>
                            </div>
                            <button
                                className="pcIconBtn pcIconBtn--close"
                                onClick={() => { setShowDuplicateModal(false); setSelectedDemande(null); }}
                                aria-label="Fermer"
                            >
                                <X size={15} />
                            </button>
                        </div>
                        <div className="pcModal__body">
                            <MissionInlineForm
                                formId="managerDuplicateMissionForm"
                                onSubmit={handleDuplicateSubmit}
                                loading={loading}
                                submitLabel="Dupliquer et envoyer l'ordre de mission'"
                                initialMissionData={buildInitialMission(selectedDemande)}
                                initialUrgent={selectedDemande.urgent}
                                initialMotif={selectedDemande.motif}
                                showNotes={false}
                                showJustificatifs={false}
                                showComment={false}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DemandeMissionManager;
